"use client";

import { type FC, type Ref, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  DocumentUploadField,
  Flex,
  Form,
  FormRow,
  Icon,
  Modal,
  Text,
  type Timeout,
  type UploadedDocument,
  usePubSub,
  useDebug,
} from "@huub-web/ui";

import { isArray } from "@huub-web/utils";

import { useTemplates } from "../../hooks/useTemplates";
import { usePageContent } from "../../hooks/usePageContent";
import { getAllowedFileMimeTypes } from "../../utils";

export interface DocumentsUploadTemplateProps {
  id?: string;
  endpoint: string;
  deleteEndpoint?: string;
  /**
   * @deprecated Use downloadEndpoint instead
   */
  viewEndpoint?: string;
  downloadEndpoint?: string;
  value: UploadedDocument[];
  label?: string;
  disabled?: boolean;
  placeholder?: string;
  description?: string;
  accepted?: ("pdf" | "doc" | "image" | "xml")[];
  processing?: boolean;
  // onStatusChange?: (
  //   status: "uploading" | "uploaded" | "deleted" | "error",
  // ) => void;
}

/**
 * A template for rendering a documents upload component.
 * @todo Multiple files uploads
 * @todo Delete file
 */
export const DocumentsUploadTemplate: FC<
  DocumentsUploadTemplateProps & { ref?: Ref<HTMLFormElement> }
> = ({
  id,
  endpoint,
  deleteEndpoint,
  viewEndpoint,
  downloadEndpoint: downloadEndpointProp,
  accepted = ["pdf", "doc", "image"],
  label,
  disabled,
  description,
  placeholder,
  // onStatusChange,
  processing,
  ref,
  ...props
}) => {
  const { uploadDocument, deleteDocument, lockUI, downloadDocument } =
    useTemplates();
  const { t } = useTranslation();
  const pubSub = usePubSub();
  const { log } = useDebug();

  const [isUploading, setIsUploading] = useState(false);
  const [value, setValue] = useState<UploadedDocument[]>(props.value ?? []);
  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] =
    useState(false);

  const downloadEndpoint = downloadEndpointProp ?? viewEndpoint;

  const uploadContentItem = useMemo(() => {
    if (!label) return null;
    const status = value[0]?.status;
    return {
      type: "data-item" as const,
      label,
      ...(description ? { description } : {}),
      meta: {
        ...(status ? { status } : { status: "niet aangeleverd" }),
        ...(value[0]?.name ? { bestand: value[0].name } : {}),
      },
    };
  }, [label, description, value]);
  usePageContent("doc-upload", uploadContentItem);

  const allowedTypes = useMemo<string[]>(
    () => getAllowedFileMimeTypes(accepted),
    [accepted],
  );

  const handleChange = (files: FileList | null, id?: string) => {
    if (files && files.length > 0) {
      setValue([{ name: files[0].name, status: "uploaded" }]);
      // onStatusChange?.("uploading");
      handleFileUpload(files[0]);
      // .then(() => {
      //   onStatusChange?.("uploaded");
      // })
      // .catch(() => {
      //   onStatusChange?.("error");
      // });
    } else {
      if (id) {
        setValue(value.filter((doc) => doc.id !== id));
        // TODO: Delete file?
      } else {
        setValue([]);
      }
    }
  };

  const handleFileUpload = async (file: File) => {
    log("purple", "DocumentsUploadTemplate", "handleFileUpload", file);

    setIsUploading(true);

    const response = await uploadDocument(endpoint, file);
    if (!response.success) {
      setValue([]);

      pubSub.publish("snackbar", {
        variant: "error",
        body: t("documentsUpload.uploadError"),
      });
    }

    setIsUploading(false);
  };

  // Delete file
  // ------------------------------

  const toBeDeletedIDRef = useRef<string | undefined>(undefined);

  const handleDelete = (id: string) => {
    toBeDeletedIDRef.current = id;
    setIsDeleteConfirmationModalOpen(true);
  };

  const handleDeleteConfirmation = async () => {
    setIsDeleteConfirmationModalOpen(false);

    if (toBeDeletedIDRef.current) {
      if (!deleteEndpoint) {
        throw new Error("[DocumentsUploadTemplate] deleteEndpoint is required");
      }

      const response = await deleteDocument(
        deleteEndpoint,
        toBeDeletedIDRef.current,
      );

      // onStatusChange?.("deleted");

      if (response.success) {
        setValue([]);
      }
    }
  };

  // Download
  // ------------------------------

  const handleDownload = async () => {
    if (!downloadEndpoint) {
      console.error("[DocumentsUploadTemplate] downloadEndpoint is required");
      return;
    }
    downloadDocument(downloadEndpoint);
  };

  // Highlighting
  // ------------------------------

  const [isHighlighted, setIsHighlighted] = useState(false);

  useEffect(() => {
    let timeout: Timeout;

    const unsubscribeHighlight = pubSub.subscribe(
      "inputHighlight",
      ({ ids }) => {
        if (id && ids.includes(id)) {
          setIsHighlighted(true);
          clearTimeout(timeout);

          timeout = setTimeout(() => {
            setIsHighlighted(false);
          }, 5000);
        }
      },
    );

    const unsubscribeUpdate = pubSub.subscribe("inputUpdate", (payload) => {
      if (!payload) return;

      const updates = isArray(payload) ? payload : [payload];

      for (const update of updates) {
        if (!update) continue;
        const { id: updateID, value } = update;
        if (id === updateID) {
          log(
            "purple",
            "DocumentsUploadTemplate",
            "pubSub(inputUpdate)",
            value,
          );
          setValue([value as unknown as UploadedDocument]);
          break;
        }
      }
    });

    return () => {
      unsubscribeHighlight();
      unsubscribeUpdate();
      clearTimeout(timeout);
    };
  }, [id]);

  return (
    <Form ref={ref}>
      <FormRow label={label} description={description} variant="vertical">
        <DocumentUploadField
          id={id}
          highlight={isHighlighted}
          placeholder={placeholder ?? t("general.selectFile")}
          loading={isUploading || processing}
          disabled={isUploading || disabled || lockUI || processing}
          value={value[0]}
          allowedTypes={allowedTypes}
          onChange={(files) => handleChange(files, value[0]?.id)}
          onDownload={downloadEndpoint ? handleDownload : undefined}
          onDelete={deleteEndpoint ? handleDelete : undefined}
        />
      </FormRow>

      {isDeleteConfirmationModalOpen && (
        <Modal
          title={t("documentsUpload.deleteModalTitle")}
          placement="center"
          onClose={() => setIsDeleteConfirmationModalOpen(false)}
          escape
        >
          <Text>{t("documentsUpload.deleteConfirmation")}</Text>
          <Flex gap="xs" justify="space-between">
            <Button
              onClick={() => setIsDeleteConfirmationModalOpen(false)}
              variant="background"
              border="default"
            >
              {t("cancel")}
            </Button>
            <Button
              onClick={handleDeleteConfirmation}
              variant="error"
              trailing={<Icon icon="delete" />}
            >
              {t("documentsUpload.confirmDelete")}
            </Button>
          </Flex>
        </Modal>
      )}
    </Form>
  );
};
